import { useEffect, useState } from "react";
import { Button, Input, Select } from "@/components/ui";
import { trpc } from "@/lib/trpc";
import { useAppStore } from "@/stores/app.store";

const FIELDS: { key: string; label: string; group: string }[] = [
  {
    key: "legalName",
    label: "Firmenwortlaut (Firmenbuch)",
    group: "Unternehmen",
  },
  { key: "legalForm", label: "Rechtsform", group: "Unternehmen" },
  { key: "firmenbuchNr", label: "Firmenbuchnummer", group: "Unternehmen" },
  {
    key: "firmenbuchGericht",
    label: "Firmenbuchgericht",
    group: "Unternehmen",
  },
  { key: "uid", label: "UID-Nummer", group: "Unternehmen" },
  { key: "addressStreet", label: "Straße", group: "Adresse" },
  { key: "addressZip", label: "PLZ", group: "Adresse" },
  { key: "addressCity", label: "Ort", group: "Adresse" },
  { key: "addressCountry", label: "Land (ISO-2)", group: "Adresse" },
  {
    key: "deliveryAddress",
    label: "Lieferadresse (Dokument-Fußzeile, optional)",
    group: "Adresse",
  },
  { key: "email", label: "E-Mail", group: "Kontakt" },
  { key: "phone", label: "Telefon", group: "Kontakt" },
  { key: "website", label: "Website", group: "Kontakt" },
  { key: "bankName", label: "Bank", group: "Bankverbindung" },
  { key: "iban", label: "IBAN", group: "Bankverbindung" },
  { key: "bic", label: "BIC", group: "Bankverbindung" },
];
const GROUPS = ["Unternehmen", "Adresse", "Kontakt", "Bankverbindung"];

export function CompanyTab() {
  const utils = trpc.useUtils();
  const org = trpc.organization.get.useQuery();
  const addToast = useAppStore((s) => s.addToast);
  const [form, setForm] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!org.data) return;
    const next: Record<string, string> = {};
    for (const f of FIELDS)
      next[f.key] =
        (org.data as Record<string, unknown>)[f.key]?.toString() ?? "";
    next.taxScheme = (org.data as any).taxScheme ?? "regel";
    next.defaultTaxRate = String((org.data as any).defaultTaxRate ?? 20);
    setForm(next);
  }, [org.data]);

  const update = trpc.organization.update.useMutation({
    onSuccess: () => {
      utils.organization.get.invalidate();
      utils.branding.get.invalidate();
      addToast({ type: "success", title: "Gespeichert" });
    },
    onError: (e) =>
      addToast({ type: "error", title: "Fehler", description: e.message }),
  });

  const onSave = () => {
    const payload: Record<string, string | number | null> = {};
    for (const f of FIELDS) {
      const v = form[f.key]?.trim() ?? "";
      payload[f.key] = v === "" ? null : v;
    }
    payload.legalName = form.legalName?.trim() || "Mavatech GmbH";
    payload.legalForm = form.legalForm?.trim() || "GmbH";
    payload.addressCountry = (form.addressCountry?.trim() || "AT")
      .toUpperCase()
      .slice(0, 2);
    payload.taxScheme = form.taxScheme || "regel";
    payload.defaultTaxRate = Number(form.defaultTaxRate || 20);
    update.mutate(payload as never);
  };

  return (
    <div className="p-6">
      <div className="max-w-3xl space-y-8">
        {GROUPS.map((group) => (
          <section key={group}>
            <h3 className="text-sm font-semibold text-text-primary mb-3">
              {group}
            </h3>
            <div className="grid grid-cols-2 gap-4">
              {FIELDS.filter((f) => f.group === group).map((f) => (
                <Input
                  key={f.key}
                  label={f.label}
                  name={f.key}
                  value={form[f.key] ?? ""}
                  onChange={(e) =>
                    setForm((s) => ({ ...s, [f.key]: e.target.value }))
                  }
                />
              ))}
            </div>
          </section>
        ))}
        <section>
          <h3 className="text-sm font-semibold text-text-primary mb-3">
            Umsatzsteuer
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <Select
              label="Steuerschema"
              value={form.taxScheme ?? "regel"}
              onChange={(e) =>
                setForm((s) => ({ ...s, taxScheme: e.target.value }))
              }
              options={[
                { value: "regel", label: "Regelbesteuerung" },
                {
                  value: "kleinunternehmer",
                  label: "Kleinunternehmer (§ 6 Abs. 1 Z 27)",
                },
              ]}
            />
            <Select
              label="Standard-USt-Satz"
              value={form.defaultTaxRate ?? "20"}
              onChange={(e) =>
                setForm((s) => ({ ...s, defaultTaxRate: e.target.value }))
              }
              options={[
                { value: "20", label: "20 %" },
                { value: "13", label: "13 %" },
                { value: "10", label: "10 %" },
                { value: "0", label: "0 %" },
              ]}
            />
          </div>
        </section>
        <div>
          <Button variant="primary" onClick={onSave} loading={update.isPending}>
            Speichern
          </Button>
        </div>
        <p className="text-xs text-text-tertiary">
          Diese Angaben erscheinen später auf Angeboten und Rechnungen (§ 11
          UStG, § 14 UGB).
        </p>
      </div>
    </div>
  );
}
